import React, { useCallback, useRef, useState } from 'react'

export type ConfirmPasswordOptions<T = void> = {
  title?: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Required checkbox shown under the password field (e.g. signature identity). */
  acknowledgement?: string
  /** Extra notice shown under the acknowledgement, or under the password when there is no checkbox. */
  notice?: string
  /**
   * Called with the entered password. Throw an Error to keep the dialog open
   * and show the message. Resolve to close the dialog and return the value.
   */
  onConfirm: (currentPassword: string) => Promise<T>
}

type ModalViewProps = {
  open: boolean
  title: string
  description: string
  confirmLabel: string
  cancelLabel: string
  password: string
  acknowledgementLabel: string
  acknowledgementChecked: boolean
  notice: string
  error: string | null
  loading: boolean
  onPasswordChange: (value: string) => void
  onAcknowledgementChange: (checked: boolean) => void
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
}

const DEFAULT_TITLE = 'Confirm your password'
const DEFAULT_DESCRIPTION = 'Re-enter your account password to continue with this sensitive action.'
const DEFAULT_CONFIRM = 'Confirm'
const DEFAULT_CANCEL = 'Cancel'

/** Presentational password step-up dialog (DaisyUI). Prefer `useConfirmPassword` for callers. */
export const ConfirmPasswordModal: React.FC<ModalViewProps> = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  password,
  acknowledgementLabel,
  acknowledgementChecked,
  notice,
  error,
  loading,
  onPasswordChange,
  onAcknowledgementChange,
  onSubmit,
  onCancel,
}) => {
  if (!open) return null

  const acknowledgementRequired = !!acknowledgementLabel
  const confirmDisabled = loading || (acknowledgementRequired && !acknowledgementChecked)

  return (
    <dialog className="modal modal-open z-[2000]">
      <div className="modal-box max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-lg">{title}</h3>
        <p className="py-2 text-sm opacity-80">{description}</p>
        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          <label className="form-control w-full">
            <span className="label-text">Current password</span>
            <input
              type="password"
              className="input input-bordered w-full"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              autoFocus
              autoComplete="current-password"
              disabled={loading}
            />
          </label>
          {acknowledgementRequired && (
            <label className="label cursor-pointer justify-start items-start gap-3">
              <input
                type="checkbox"
                className="checkbox checkbox-primary shrink-0 mt-0.5"
                checked={acknowledgementChecked}
                onChange={(e) => onAcknowledgementChange(e.target.checked)}
                disabled={loading}
              />
              <span className="label-text text-sm whitespace-normal text-left leading-snug">
                {acknowledgementLabel}
              </span>
            </label>
          )}
          {notice && (
            <p className="text-xs text-base-content/70 leading-relaxed">{notice}</p>
          )}
          {error && (
            <div className="alert alert-error text-sm py-2">
              <span>{error}</span>
            </div>
          )}
          <div className="modal-action">
            <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={loading}>
              {cancelLabel}
            </button>
            <button type="submit" className="btn btn-primary" disabled={confirmDisabled}>
              {loading ? 'Verifying…' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onCancel} disabled={loading}>
          close
        </button>
      </form>
    </dialog>
  )
}

type PendingRequest<T> = {
  options: ConfirmPasswordOptions<T>
  resolve: (value: T | null) => void
}

/**
 * Imperative password confirmation for sensitive actions.
 *
 * @example
 * const { confirmPassword, confirmPasswordModal } = useConfirmPassword()
 * // ...
 * const result = await confirmPassword({
 *   description: 'Confirm to reset passkeys.',
 *   onConfirm: (password) => authService.mfaResetPasskeys(password),
 * })
 * if (result === null) return // cancelled
 * // render {confirmPasswordModal} once in the tree
 */
export function useConfirmPassword() {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState(DEFAULT_TITLE)
  const [description, setDescription] = useState(DEFAULT_DESCRIPTION)
  const [confirmLabel, setConfirmLabel] = useState(DEFAULT_CONFIRM)
  const [cancelLabel, setCancelLabel] = useState(DEFAULT_CANCEL)
  const [password, setPassword] = useState('')
  const [acknowledgementLabel, setAcknowledgementLabel] = useState('')
  const [acknowledgementChecked, setAcknowledgementChecked] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const pendingRef = useRef<PendingRequest<unknown> | null>(null)

  const resetLocalState = useCallback(() => {
    setPassword('')
    setAcknowledgementChecked(false)
    setError(null)
    setLoading(false)
  }, [])

  const close = useCallback((result: unknown | null) => {
    const pending = pendingRef.current
    pendingRef.current = null
    setOpen(false)
    resetLocalState()
    pending?.resolve(result as never)
  }, [resetLocalState])

  const confirmPassword = useCallback(<T,>(options: ConfirmPasswordOptions<T>): Promise<T | null> => {
    return new Promise<T | null>((resolve) => {
      // Replace any in-flight prompt (treat as cancelled).
      if (pendingRef.current) {
        pendingRef.current.resolve(null)
      }
      pendingRef.current = { options: options as ConfirmPasswordOptions<unknown>, resolve: resolve as (v: unknown | null) => void }
      setTitle(options.title || DEFAULT_TITLE)
      setDescription(options.description || DEFAULT_DESCRIPTION)
      setConfirmLabel(options.confirmLabel || DEFAULT_CONFIRM)
      setCancelLabel(options.cancelLabel || DEFAULT_CANCEL)
      setAcknowledgementLabel(options.acknowledgement || '')
      setNotice(options.notice || '')
      resetLocalState()
      setOpen(true)
    })
  }, [resetLocalState])

  const onCancel = useCallback(() => {
    if (loading) return
    close(null)
  }, [close, loading])

  const onSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    const pending = pendingRef.current
    if (!pending) return

    if (!password.trim()) {
      setError('Enter your current password to continue.')
      return
    }
    if (acknowledgementLabel && !acknowledgementChecked) {
      setError('Check the acknowledgement to continue.')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await pending.options.onConfirm(password)
      close(result)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Password confirmation failed.'
      setError(message)
      setLoading(false)
    }
  }, [acknowledgementChecked, acknowledgementLabel, close, password])

  const confirmPasswordModal = (
    <ConfirmPasswordModal
      open={open}
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      password={password}
      acknowledgementLabel={acknowledgementLabel}
      acknowledgementChecked={acknowledgementChecked}
      notice={notice}
      error={error}
      loading={loading}
      onPasswordChange={setPassword}
      onAcknowledgementChange={setAcknowledgementChecked}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )

  return { confirmPassword, confirmPasswordModal }
}

export default ConfirmPasswordModal
